import React, { useState } from 'react';
// redux hooks
import { useFieldArray, Controller, useWatch } from 'react-hook-form';
// BASE components
import BASESvgIcon from '../../../../../../../@BASE/core/BASESvgIcon';
import BASEUtils from '../../../../../../../@BASE/utils/BASEUtils';
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/system/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TextField from '@mui/material/TextField';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from '@mui/material/Typography';
import { Tabs, Tab } from '@mui/material';
// custom components
import PECoordinates from './PECoordinates';
import NumberedHeading from '../../../../../../shared-components/forms/NumberedHeading';
// data
import { ReviewData } from '../../../data/Defaults';

export default function PECoordinateTab({
  control,
  errors,
  setValue,
  lessonLabel,
  getValues
}) {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: `content.povs`
  });
  const [tab, setTab] = useState(0);

  const watchedPOVs = useWatch({ control, name: 'content.povs' });

  const handleChange = (event, newTab) => {
    setTab(newTab);
  };

  const coreItems = useWatch({ control, name: 'content.core' }) || [];

  const getUniqueObjects = () => {
    const idCount = new Map();
    coreItems.forEach((item) => {
      const obj = item?.object;
      if (obj?.id) idCount.set(obj.id, (idCount.get(obj.id) || 0) + 1);
    });

    const seen = new Set();
    const uniques = [];
    coreItems.forEach((item, idx) => {
      const obj = item?.object;
      if (obj?.id && !seen.has(obj.id)) {
        seen.add(obj.id);
        const count = idCount.get(obj.id);
        uniques.push({
          id: obj.id,
          label: obj.label || `Object ${idx + 1}`,
          count
        });
      }
    });
    return uniques;
  };

  const ORIGINAL_WIDTH = 1920;
  const ORIGINAL_HEIGHT = 1080;

  function computeGrid(n) {
    const cols = Math.ceil(Math.sqrt(n)); // as square as possible
    const rows = Math.ceil(n / cols);
    return { cols, rows };
  }

  function defaultQuadForIndex(i, n) {
    const { cols, rows } = computeGrid(n);
    const cellW = ORIGINAL_WIDTH / cols;
    const cellH = ORIGINAL_HEIGHT / rows;

    const col = i % cols;
    const row = Math.floor(i / cols);

    // Rectangle size scales with cell, with sane bounds
    const rectW = Math.max(140, Math.min(cellW * 0.72, 520));
    const rectH = Math.max(120, Math.min(cellH * 0.58, 320));

    // Center inside its cell
    const x1 = col * cellW + (cellW - rectW) / 2;
    const y1 = row * cellH + (cellH - rectH) / 2;
    const x2 = x1 + rectW;
    const y2 = y1 + rectH;

    return [
      { x: x1, y: y1 }, // TL
      { x: x2, y: y1 }, // TR
      { x: x2, y: y2 }, // BR
      { x: x1, y: y2 } // BL
    ];
  }

  const handleCreateViews = (placeholder = false) => {
    const uniques = getUniqueObjects();
    const n = uniques.length;

    const viewExtensionArray = [
      '_imm_c',
      '_imm_l',
      '_imm_r',
      '_iso_c',
      '_iso_l',
      '_iso_r'
    ];

    const safeLabel = lessonLabel.toLowerCase().replace(/-/g, '_');

    const sixViews = Array.from({ length: 6 }).map((_, viewIndex) => {
      const targets = placeholder
        ? uniques.map((obj, objIndex) => ({
            id: obj.id,
            points: defaultQuadForIndex(objIndex, n)
          }))
        : [];

      const labelToUse = placeholder
        ? `${safeLabel}_placeholder`
        : `${getValues('label').toLowerCase().replace(/-/g, '_')}${
            viewExtensionArray[viewIndex]
          }`;

      return {
        id: BASEUtils.generateGUID(),
        review: ReviewData,
        img: labelToUse,
        targets
      };
    });

    setValue('content.povs', sixViews, {
      shouldDirty: true,
      shouldValidate: true
    });
    setTab(0);
  };

  const handleDeleteViews = () => {
    setValue('content.povs', [], {
      shouldDirty: true,
      shouldValidate: true
    });
    setTab(0);
  };

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0
          }
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className="pr-0">
                <Accordion
                  defaultExpanded
                  className="bg-transparent shadow-none"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${index}`}
                    id={`${index}`}
                    className="py-0 px-16 m-0 rounded bg-white shadow-sm"
                    sx={{
                      border: 'solid 1px',
                      borderColor: '#bdbdbd'
                    }}
                  >
                    <Typography className="m-0 font-semibold">
                      View {index + 1}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className="p-0">
                    <Box className="p-0">
                      <NumberedHeading
                        number="1"
                        title="File Name"
                        instructions="The File Name, file must be a valid 16:9 png but do not include .png in the name."
                        required
                      />
                      <Controller
                        control={control}
                        name={`content.povs.${index}.img`}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              className="mt-16 mb-16"
                              label="File Name"
                              placeholder="File Name"
                              error={!!errors.content?.povs?.[index]?.img}
                              helperText={
                                errors.content?.povs?.[index]?.img?.message
                              }
                              fullWidth
                              required
                            />
                          </>
                        )}
                      />
                    </Box>
                    <Box
                      className="flex items-center mt-32 p-0 border-t"
                      sx={{ backgroundColor: 'background.default' }}
                    >
                      {index !== 0 && (
                        <Button
                          className="group flex items-center mt-2 -ml-4 mr-8 py-2 px-4 rounded cursor-pointer"
                          onClick={() => move(index, index - 1)}
                        >
                          <BASESvgIcon size={20}>
                            heroicons-solid:arrow-circle-up
                          </BASESvgIcon>
                          <span className="ml-8 font-medium text-secondary group-hover:underline">
                            Move Up
                          </span>
                        </Button>
                      )}
                      {index !== fields.length - 1 && (
                        <Button
                          className="group flex items-center mt-2 -ml-4 mr-8 py-2 px-4 rounded cursor-pointer"
                          onClick={() => move(index, index + 1)}
                        >
                          <BASESvgIcon size={20}>
                            heroicons-solid:arrow-circle-down
                          </BASESvgIcon>
                          <span className="ml-8 font-medium text-secondary group-hover:underline">
                            Move Down
                          </span>
                        </Button>
                      )}
                      <Button
                        className="group flex items-center mt-2 py-2 px-4 rounded cursor-pointer ml-auto"
                        onClick={() => remove(index)}
                      >
                        <BASESvgIcon size={20}>
                          heroicons-solid:minus-circle
                        </BASESvgIcon>
                        <span className="ml-8 font-medium text-secondary group-hover:underline">
                          Remove Object
                        </span>
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-2 -ml-4 py-2 px-4 rounded cursor-pointer"
          onClick={() =>
            append({
              id: BASEUtils.generateGUID(),
              review: ReviewData,
              img: '',
              targets: [
                {
                  id: '',
                  points: []
                }
              ]
            })
          }
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add View
          </span>
        </Button>
        {watchedPOVs.length === 0 ? (
          <>
            <Button
              className="group flex items-center mt-2 -ml-4 py-2 px-4 rounded cursor-pointer"
              onClick={() => handleCreateViews(true)}
            >
              <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
              <span className="ml-8 font-medium text-secondary group-hover:underline">
                Create Placeholders
              </span>
            </Button>
            <Button
              className="group flex items-center mt-2 -ml-4 py-2 px-4 rounded cursor-pointer"
              onClick={() => handleCreateViews(false)}
            >
              <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
              <span className="ml-8 font-medium text-secondary group-hover:underline">
                Create Proper View Names
              </span>
            </Button>
          </>
        ) : (
          <Button
            className="group flex items-center -ml-4 py-2 px-4 rounded cursor-pointer"
            color="error"
            onClick={handleDeleteViews}
          >
            <BASESvgIcon size={20}>heroicons-solid:trash</BASESvgIcon>
            <span className="ml-8 font-medium text-secondary group-hover:underline">
              Purge All Views
            </span>
          </Button>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={handleChange}
        variant="scrollable"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        {watchedPOVs.map((_, index) => (
          <Tab key={index} label={`View ${index + 1}`} />
        ))}
      </Tabs>

      {/* Tab Panels */}
      {watchedPOVs.map((item, index) => (
        <div
          key={index}
          role="tabpanel"
          style={{ display: tab === index ? 'block' : 'none' }}
        >
          <PECoordinates
            {...{ control, errors, setValue }}
            imageName={item.img}
            selectedPovIndex={index}
          />
        </div>
      ))}
    </>
  );
}
